import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { select, Store } from "@ngrx/store";
import { Observable, Subscription, BehaviorSubject, EMPTY } from 'rxjs';
import { map, skip, take, switchMap, shareReplay, tap, catchError, finalize } from 'rxjs/operators';
import {
  ColumnSorterInfo,
  DefaultPage,
  FieldType,
  FilterEvent,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { ProductDTO } from "src/app/models/Products/productDTO";
import { ProductService } from "src/app/shared/services/products/products/product.service";
import { getColorByBgColor } from "src/app/shared/utils/colorUtils";

import { setColumnInfoCategoriesProperties } from "src/app/shared/utils/tableUtils";
import { getProductsList } from "src/app/store/actions/products/product.actions";
import {
  AppState,
  loadedProductsTableConfig,
  loadedProductIsLoadingState,
  loadedProductListState,
  loadedProductTotalState,
} from "src/app/store/reducers";

@Component({
  selector: "app-select-products-dialog",
  templateUrl: "./select-products-dialog.component.html",
  styleUrls: ["./select-products-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SelectProductsDialogComponent implements OnInit {
  public isLoading$ = new BehaviorSubject<boolean>(false);

  public columnInfo$: Observable<ColumnSorterInfo[]>;
  public headerIcon: string;

  public productsList$: Observable<ProductDTO[]>;
  public productsList: ProductDTO[];
  public totalProducts: number;
  public preselectedIds: string[] = []; 

  private tableEvent$ = new BehaviorSubject<TableEvent>(null);
  private productsListSub: Subscription;

  private loadedProductsSub: Subscription;

  initialLinkedSet: Set<string> = new Set();
  private selectedProductsMap: { [code: string]: ProductDTO } = {};
  public selectedRow: object = {};

  public selectedProducts: ProductDTO[] = [];
  isAllSelected = false;

  public tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Products.code",
      field: "code",
      fieldType: FieldType.STRING,
      hidden: true,
      identifier: true,
      searchable: true,
    },
    {
      header: "Products.description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Products.shortDescription",
      field: "shortDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Products.productGroupCode",
      field: "group.code",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Products.productGroupDescription",
      field: "group.description",
      fieldType: FieldType.STRING,
    },
    {
      header: "Products.productFamilyCode",
      field: "family.code",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Products.productFamilyDescription",
      field: "family.description",
      fieldType: FieldType.STRING,
    },
    {
      header: "Products.tax",
      field: "taxDescription",
      fieldType: FieldType.STRING,
    },
    {
      header: "Products.imageUrl",
      field: "imageUrl",
      fieldType: FieldType.IMAGE,
    },
    {
      header: "PosMovements.unitPriceNet",
      field: "unitPriceNet",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.CURRENCY,
      fuelFormatType: FormatType.FUELUNITPRICE,
      hidden: true,
    },
    {
      header: "Products.sitePrice",
      field: "productSiteUnitPriceGross",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.CURRENCY,
      fuelFormatType: FormatType.FUELUNITPRICE,
    },
    {
      header: "PosMovements.unitPriceGross",
      field: "unitPriceGross",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.CURRENCY,
      fuelFormatType: FormatType.FUELUNITPRICE,
      hidden: true,
    },
    {
      header: "Products.color",
      field: "color",
      fieldType: FieldType.CHIP_STRING,
      hidden: true,
    },
    {
      header: "Products.productUnitDescription",
      field: "unit.description",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Products.defaultQuantity",
      field: "defaultQuantity",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Products.refundMaxPeriod",
      field: "refundMaxPeriod",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Products.productUnitEnabled",
      field: "unit.enabled",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.productGroupEnabled",
      field: "group.enabled",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.isFuel",
      field: "isFuel",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.promptQuantity",
      field: "promptQuantity",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.promptPrice",
      field: "promptPrice",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.priceByWeight",
      field: "priceByWeight",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.fiscalInfoRequired",
      field: "fiscalInfoRequired",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.refundable",
      field: "refundable",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.canSellBelowBuyPrice",
      field: "canSellBelowBuyPrice",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.packs",
      field: "composite",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.enabled",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Products.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "Products.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { 
      preselectedProducts?: ProductDTO[] 
      advancedFilter?: any[]
      siteCode: string
    },
    private dialogRef: MatDialogRef<SelectProductsDialogComponent>,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private productService: ProductService,
  ) {}

  ngOnInit(): void {
    this.columnInfo$ = this.store.pipe(select(loadedProductsTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          return setColumnInfoCategoriesProperties(
            columnInfo,
            this.tableConfig
          );
        }
      })
    );

    this.productsList$ = this.tableEvent$.pipe(
      tap(() => this.isLoading$.next(true)),
      switchMap((tableEvent) =>
        this.fetchProductsList(this.data.siteCode, tableEvent).pipe(
          finalize(() => this.isLoading$.next(false))
        )
      ),
      map((products: ProductDTO[]) => (products || []).map((p) => this.setCustomProductChip(p))),
      shareReplay(1)
    );

    if (this.data?.preselectedProducts?.length) {
      this.data.preselectedProducts.forEach(p => {
        this.initialLinkedSet.add(p.code);
      });
      this.selectedProducts = Object.values(this.initialLinkedSet);
      this.preselectedIds = [...this.initialLinkedSet];
    }

    this.productsListSub = this.productsList$.subscribe(products => {
      if (!products) return;
      this.productsList = products;
      this.updateSelectAllState(products);
    });

  }

  onProductCheckBoxChange(product: ProductDTO, event: MatCheckboxChange){
    const isNowChecked = event.checked;
    const wasInitialChecked = this.initialLinkedSet.has(product.code);

    if(isNowChecked === wasInitialChecked) {
      delete this.selectedProductsMap[product.code]
    } else {
      this.selectedProductsMap[product.code] = {
        code: product.code,
        shouldBeLinked: isNowChecked
      };
    }
  }

  selectAllChanged(event: MatCheckboxChange) {
    this.productsList$.pipe(take(1)).subscribe(products => {
    
      products.forEach(p => this.onProductCheckBoxChange(p, event));

      this.selectedProducts = Object.values(this.selectedProductsMap);
      this.preselectedIds = this.getCurrentlyCheckedIds();
      
      requestAnimationFrame(() => {
        this.productsList = [...this.productsList];
        this.cdr.detectChanges();
      });
      this.updateSelectAllState(products);
    });
  }

  private getCurrentlyCheckedIds(): string[] {
    const allKnownCodes = new Set<string>([
      ...this.initialLinkedSet,
      ...Object.keys(this.selectedProductsMap)
    ]);

    return Array.from(allKnownCodes).filter(code => this.isCurrentlyChecked(code));
  }

  isCurrentlyChecked(code: string){
    if(code in this.selectedProductsMap){
      return this.selectedProductsMap[code].shouldBeLinked;
    }
    return this.initialLinkedSet.has(code);
  }

  updateSelectAllState(products: ProductDTO[]) {
    this.isAllSelected =
      products.length > 0 &&
      products.every(p => this.isCurrentlyChecked[p.code]);
      this.cdr.markForCheck();
  }

  public onGetTableData(tableEvent: TableEvent): void {
    this.tableEvent$.next(tableEvent);
  }

  private fetchProductsList(
    siteCode: string,
    tableEvent?: TableEvent
  ): Observable<ProductDTO[]> {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent || {};
    const { pageIndex, pageSize } = pageEvent || {};
 
    let mergedFilterEvent = filterEvent;
    if (this.data.advancedFilter) {
      const currentFilters = filterEvent?.advancedFilter || [];
      mergedFilterEvent = {
        ...filterEvent,
        advancedFilter: [...this.data.advancedFilter, ...currentFilters]
      };
    }
 
    return this.productService.getProductSearchBySite(
      siteCode,
      {
        skip: (pageIndex * pageSize) || DefaultPage.PAGE_INDEX,
        top: pageSize ?? DefaultPage.PAGE_SIZE,
        realSize: true,
      },
      searchEvent,
      sortEvent,
      mergedFilterEvent
    ).pipe(
      map((response) => {
        this.totalProducts = response.size;
        return response.items;
      }),
      catchError((error) => {
        return EMPTY;
      })
    );
  }

  setCustomProductChip(product: any) {
    const defaultProduct = {
      ...product,
    };

    const { color } = product;

    if (color === undefined) {
      return defaultProduct;
    }

    if (color !== undefined && color !== null) {
      let bgColor = getColorByBgColor(`#${color}`);
      let productColor = {
        ...product,
        color: {
          value: color,
          icon: "format_color_fill",
          style: `background-color: #${color}; color: ${bgColor};`,
        },
      };
      return productColor;
    }
  }

  public onPageSelection(currentPageSelected: ProductDTO[]) {
    currentPageSelected.forEach((p) => {
      this.selectedProductsMap[p.code] = p;
    });
    this.selectedProducts = Object.values(this.selectedProductsMap);
  }

  public onIdsUpdate(allIds: string[]) {
    const idSet = new Set(allIds);
    Object.keys(this.selectedProductsMap).forEach((code) => {
      const isNowSelected = idSet.has(code);
      const wasInitiallyLinked = this.initialLinkedSet.has(code);

    if (isNowSelected === wasInitiallyLinked) {
      delete this.selectedProductsMap[code];
    } else if (this.selectedProductsMap[code].shouldBeLinked !== isNowSelected) {
      this.selectedProductsMap[code].shouldBeLinked = isNowSelected;
    }
    });
    this.selectedProducts = Object.values(this.selectedProductsMap);
  }

  public onRowSelectionMultiple(currentPageSelected: ProductDTO[]): void {
    if (!this.productsList) return;

    const currentPageSelectedCodes = new Set(
      (currentPageSelected || []).map((p) => p.code)
    );

    this.productsList.forEach((p) => {
      const isNowSelected = currentPageSelectedCodes.has(p.code);
      const wasInitiallyLinked = this.initialLinkedSet.has(p.code);

      if (isNowSelected === wasInitiallyLinked) {
        delete this.selectedProductsMap[p.code];
      } else {
        this.selectedProductsMap[p.code] = {
          ...p,
          shouldBeLinked: isNowSelected
        };
      }
    });

    this.selectedProducts = Object.values(this.selectedProductsMap);
  }

  public isSelectedRowEmpty(): boolean {
    return Object.keys(this.selectedProductsMap).length === 0;
  }

  public onConfirmClick(): void {
    this.dialogRef.close(this.selectedProducts);
  }

  ngOnDestroy() {
    if (this.loadedProductsSub) {
      this.loadedProductsSub.unsubscribe();
    }
    if (this.productsListSub) {
      this.productsListSub.unsubscribe();
    }
  }
}
